import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import type { SICReportStatus, SICReportRunStatus } from '@/types/sic-report.types';

interface Props {
    status: SICReportStatus | null;
    isLoading: boolean;
}

const STATUS_VARIANT: Record<SICReportRunStatus, { label: string; color: string }> = {
    RUNNING: { label: 'En curso', color: 'bg-blue-500/20 text-blue-400' },
    COMPLETED: { label: 'Completado', color: 'bg-emerald-500/20 text-emerald-400' },
    COMPLETED_WITH_ERRORS: { label: 'Con alertas', color: 'bg-amber-500/20 text-amber-400' },
    FAILED: { label: 'Fallo', color: 'bg-red-500/20 text-red-400' },
};

function formatTimestamp(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}

function nextScheduledRun(): string {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(3, 0, 0, 0);
    return tomorrow.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}

function StatBox({ label, value, color = 'text-slate-200' }: { label: string; value: number; color?: string }) {
    return (
        <div className="rounded border border-slate-700 bg-slate-900/40 px-3 py-2">
            <div className="text-xs text-slate-400">{label}</div>
            <div className={`text-xl font-semibold ${color}`}>{value}</div>
        </div>
    );
}

export function SICStatusCard({ status, isLoading }: Props) {
    if (isLoading) {
        return (
            <Card title="Estado actual" description="Resumen de la ultima corrida">
                <Skeleton className="h-24" />
            </Card>
        );
    }

    if (!status) {
        return (
            <Card title="Estado actual" description="Resumen de la ultima corrida">
                <p className="text-sm text-slate-400">Aun no hay corridas para este partner.</p>
                <p className="text-xs text-slate-500 mt-2">Proxima corrida automatica: {nextScheduledRun()}</p>
            </Card>
        );
    }

    const variant = STATUS_VARIANT[status.status];

    return (
        <Card title="Estado actual" description="Resumen de la ultima corrida">
            <div className="space-y-3">
                <div className="flex items-center gap-3">
                    <Badge className={variant.color}>{variant.label}</Badge>
                    <div className="text-sm text-slate-400">
                        Ultima: <span className="text-slate-200">{formatTimestamp(status.completed_at ?? status.started_at)}</span>
                    </div>
                </div>
                <div className="text-xs text-slate-500">
                    Proxima corrida automatica: {nextScheduledRun()}
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                    <StatBox label="Elegibles" value={status.eligible_count} />
                    <StatBox label="Saltados" value={status.skipped_count} color="text-slate-400" />
                    <StatBox label="Enviados" value={status.sent_count} />
                    <StatBox label="Confirmados" value={status.confirmed_count} color="text-emerald-400" />
                    <StatBox label="Rechazados" value={status.rejected_count} color="text-amber-400" />
                    <StatBox label="Errores" value={status.error_count} color="text-red-400" />
                </div>
            </div>
        </Card>
    );
}
